import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GetRetailerTypes,
  addRetailerType,
  updateRetailerType
} from "../../apis/RetailerApi";
import { BrandsApi } from "../../apis/BrandsApi";
import { toast } from "react-toastify";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import { useDispatch, useSelector } from "react-redux";
import CircularIndeterminateModal from "../../utils/Loading";
import TableHeader from "components/Table/TableHeader";
import TableElement from "components/Table/TableElement";
import { setLoadingPanel } from "../../redux/features/loading";
import Pagination from "@mui/material/Pagination";
import CustomDropdown from "../../components/CustomDropdown";
import { ActionButton, SearchInput, Toolbar as UIToolbar, TableCard, FormCard } from "utils/ui";

export default function RetailerTypes() {
  const [open, setOpen] = useState(false);
  const [retailers, setRetailers] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    brandId: "",
    brandName: "",
    active: true
  });
  const [brands, setBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBrand, setSearchBrand] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loadingReducer.isLoading);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      const performSearch = async () => {
        setIsSearching(true);
        try {
          await fetchRetailerTypes({
            keyword: debouncedSearchTerm || null,
            page: 0,
            brandId: searchBrand?.id || null
          });
        } finally {
          setIsSearching(false);
        }
      };

      performSearch();
    }
  }, [debouncedSearchTerm, searchBrand?.id]);

  const handleClickOpen = () => {
    setOpen(true);
    setFormData({
      id: "",
      name: "",
      description: "",
      brandId: "",
      brandName: "",
      active: true
    });
    setIsEdit(false);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      id: "",
      name: "",
      description: "",
      brandId: "",
      brandName: "",
      active: true
    });
    setIsEdit(false);
  };

  const fetchRetailerTypes = async (searchParams = {}) => {
    if (searchParams._skipLoading !== true) {
      dispatch(setLoadingPanel(true));
    }
    
    try {
      const defaultParams = {
        page: searchParams.page !== undefined ? searchParams.page : page,
        size: pageSize,
        ...searchParams
      };

      delete defaultParams._skipLoading;
      
      const res = await GetRetailerTypes(defaultParams);
      setRetailers(res.data?.data || []);
      setTotalPages(res.data?.pages || 1);
      return res.data?.data || [];
    } catch (error) {
      toast("Error fetching retailer types", { type: "error" });
      return [];
    } finally {
      if (searchParams._skipLoading !== true) {
        dispatch(setLoadingPanel(false));
      }
    }
  };


  useEffect(() => {
    const loadInitialData = async () => {
      dispatch(setLoadingPanel(true));
      try {
        const res = await GetRetailerTypes({ 
          page: 0, 
          size: pageSize,
          keyword: null 
        });
        setRetailers(res.data?.data || []);
        setTotalPages(res.data?.pages || 1);
        

        const brandRes = await BrandsApi.GetBrandsListByFilter({ page: 0, size: 999 });
        const brandsArray = brandRes.data?.data || [];
        setBrands(brandsArray);
      } catch (error) {
        toast("Error loading initial data", { type: "error" });
      } finally {
        dispatch(setLoadingPanel(false));
      }
    };
    
    loadInitialData();
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        const timeoutId = searchTimeoutRef.current;
        clearTimeout(timeoutId);
      }
    };
  }, []);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSave = async () => {
    try {
      dispatch(setLoadingPanel(true));
      const payload = {
        id: formData.id || null,
        name: formData.name,
        description: formData.description,
        brandId: formData.brandId
      };
      
      if (isEdit) {
        await updateRetailerType(payload);
      } else {
        await addRetailerType(payload);
      }

      toast(isEdit ? "Retailer Type Updated" : "Retailer Type Added", {
        type: "success",
        position: toast.POSITION.TOP_CENTER,
        autoClose: 1000,
      });
      
      setOpen(false);
      setFormData({
        id: "",
        name: "",
        description: "",
        brandId: "",
        brandName: "",
        active: true
      });
      
      setPage(0);
      await fetchRetailerTypes({ page: 0 });
      
    } catch (err) {
      toast(err?.response?.data?.message || "Error saving retailer type", {
        type: "error",
        position: toast.POSITION.TOP_CENTER,
        autoClose: 1000,
      });
    } finally {
      dispatch(setLoadingPanel(false));
    }
  };

  const handleEdit = (retailer) => {
    setFormData({
      id: retailer.id || "",
      name: retailer.name || "",
      description: retailer.description || "",
      brandId: retailer.brand?.id || "",
      brandName: retailer.brand?.name || "",
      active: retailer.isActive !== false
      
    });
    setIsEdit(true);
    setOpen(true);
  };

  const handlePageChange = async (event, value) => {
    setPage(value - 1);
    await fetchRetailerTypes({ 
      page: value - 1, 
      brandId: searchBrand?.id,
      keyword: searchTerm || null
    });
  };

  const handleBrandSearch = async (selectedBrand) => {
    setSearchBrand(selectedBrand);
    setPage(0);
    await fetchRetailerTypes({
      page: 0,
      brandId: selectedBrand?.id || null,
    });
  };

  return (
    <>
      <CircularIndeterminateModal open={isLoading || isSearching} />
      <FormCard>
        <UIToolbar>
          <SearchInput 
            placeholder="Search by Retailer Type Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            // style={{ minWidth: 220 }}
          />            
          <div className="w-full sm:flex-1 sm:min-w-[240px]">
            <CustomDropdown
              options={brands}
              value={searchBrand?.id || ""}
              onChange={handleBrandSearch}
              getOptionLabel={(option) => option.name || ""}
              getOptionValue={(option) => option.id}
              label="Search by Brand"
              placeholder="Type to search brands..."
              fullWidth={true}
            />
          </div>
          <ActionButton icon="fas fa-plus" onClick={handleClickOpen}>Add Retailer Type</ActionButton>
        </UIToolbar>
      </FormCard>
      <TableCard>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px 0 8px 0",
          }}
        >
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
            size="medium"
          />
        </div>
        <div className="overflow-x-auto py-3 px-4 lg:p-8">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <TableHeader heading="Name" />
                <TableHeader heading="Description" />
                <TableHeader heading="Brand Name" />
                <TableHeader heading="Active" />
                <TableHeader heading="Created On" />
                <TableHeader heading="Last Updated On" />
                <TableHeader heading="Action" />
              </tr>
            </thead>
            <tbody>
              {retailers
                .map((retailer, i) => (
                  <tr
                    key={retailer.id}
                    style={{ transition:"background 100ms" }} onMouseEnter={(e)=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
                  >
                    <TableElement value={retailer.name} />
                    <TableElement value={retailer.description} />
                    <TableElement value={retailer.brand?.name || ""} />
                    <TableElement value={retailer.isActive !== false ? "Yes" : "No"} />
                    <TableElement value={retailer.createdOn} />
                    <TableElement value={retailer.lastUpdatedOn} />
                    <TableElement
                      value={
                        <span
                          className="cursor-pointer text-sky-500 hover:text-sky-950 font-bold"
                          onClick={() => {
                            handleEdit(retailer);
                          }}
                        >
                          EDIT
                        </span>
                      }
                    />
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </TableCard>
      <Dialog fullScreen open={open} onClose={handleClose}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} component="div">
              {isEdit
                ? `Edit Retailer Type - ${formData.name}`
                : "Add Retailer Type"}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
          <form>
            <div style={{ borderRadius: 8, padding: 0, marginBottom: 24 }}>
              <TextField
                required
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
                multiline
                rows={4}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <CustomDropdown
                  options={brands}
                  value={formData.brandId}
                  onChange={(selectedBrand) => {
                    setFormData({
                      ...formData,
                      brandId: selectedBrand ? selectedBrand.id : "",
                      brandName: selectedBrand ? selectedBrand.name : "",
                    });
                  }}
                  getOptionLabel={(option) => option.name || ""}
                  getOptionValue={(option) => option.id}
                  label="Brand"
                  helpText="Search brands by typing"
                  placeholder="Type to search brands..."
                  fullWidth
                  sx={{ mb: 0 }}
                />
              </FormControl>
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave}>
            {isEdit ? "Save Changes" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
